/**
 * Access Management Store
 *
 * Simplified store for managing organization members.
 * Transforms flat API structure into organization-grouped structure.
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';

import { subAccountsAPI, type AccessManagementView } from '@/api/sub-accounts-api';

/**
 * Member interface
 */
export interface Member {
  userId: string;
  email: string;
  role: string;
}

/**
 * Organization interface with embedded members
 */
export interface Organization {
  orgId: string;
  orgName: string;
  userRole: string; // Current user's role in this org
  members: Member[];
}

export const useAccessManagementStore = defineStore('accessManagement', () => {
  // ============ State ============

  const organizations = ref<Organization[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // ============ Getters ============

  const hasOrganizations = computed(() => organizations.value.length > 0);

  const getOrganization = computed(() => (orgId: string) =>
    organizations.value.find(org => org.orgId === orgId)
  );

  // ============ Actions ============

  /**
   * Fetch access management data and transform into grouped structure
   */
  async function fetchAccessManagementData(): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const view: AccessManagementView = await subAccountsAPI.getAccessManagementView();

      // Transform flat structure into organization-grouped structure
      organizations.value = view.managedOrganizations.map(org => {
        // Get all members for this organization
        const orgMembers = view.organizationMembers
          .filter(member => member.orgId === org.orgId)
          .map(member => ({
            userId: member.userId,
            email: member.email,
            role: member.role,
          }));

        return {
          orgId: org.orgId,
          orgName: org.orgName,
          userRole: org.role,
          members: orgMembers,
        };
      });

      console.log('✅ Access management data loaded', {
        organizationsCount: organizations.value.length,
      });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to load access management data';
      error.value = errorMsg;
      toast.error(errorMsg);
      console.error('❌ Failed to load access management data:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Invite a member to an organization
   */
  async function inviteMember(
    orgId: string,
    email: string,
    role: 'MEMBER' | 'VIEWER'
  ): Promise<void> {
    try {
      await subAccountsAPI.inviteUserToOrganization(orgId, { email, role });
      toast.success('Member invited successfully');
      await fetchAccessManagementData();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to invite member';
      toast.error(errorMsg);
      console.error('❌ Failed to invite member:', err);
      throw err;
    }
  }

  /**
   * Remove a member from an organization
   */
  async function removeMember(orgId: string, userId: string): Promise<void> {
    try {
      await subAccountsAPI.removeOrganizationMember(orgId, userId);
      toast.success('Member removed successfully');
      await fetchAccessManagementData();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to remove member';
      toast.error(errorMsg);
      console.error('❌ Failed to remove member:', err);
      throw err;
    }
  }

  /**
   * Update member role in an organization
   */
  async function updateMemberRole(
    orgId: string,
    userId: string,
    newRole: string
  ): Promise<void> {
    try {
      await subAccountsAPI.updateMemberRole(orgId, userId, newRole);
      toast.success('Member role updated successfully');
      await fetchAccessManagementData();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to update role';
      toast.error(errorMsg);
      console.error('❌ Failed to update role:', err);
      throw err;
    }
  }

  function resetState(): void {
    organizations.value = [];
    isLoading.value = false;
    error.value = null;
  }

  return {
    // State
    organizations,
    isLoading,
    error,

    // Getters
    hasOrganizations,
    getOrganization,

    // Actions
    fetchAccessManagementData,
    inviteMember,
    removeMember,
    updateMemberRole,
    resetState,
  };
});
